import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { cn } from "../../../common/u1/cn";
import { Modal } from "../../../common/Modal";
import { CommonToast } from "../../../common/Toast";
import { Input } from "../../../common/u1/Input";
import { SelectField } from "../../../common/u1/SelectField";
import { Button } from "../../../common/u1/Button";
import { CompanyMark } from "../../../common/u1/CompanyMark";
import { CloseIcon } from "../../../../assets/ui/icons";
import {
  useGetCompanyUserListMutation,
  useCreateCompanyUserInviteMutation,
  useCreateCompanyUserRoleUpdateMutation,
  useCreateCompanyUserRoleDeleteMutation,
} from "../../../../store/Company/companyApiSlice";

/* Manage Roles. TRANSCRIBED from BannerCompanyCard: the same four calls (list, invite, role update, delete), the same request shapes, the same toasts, and the... */

/* Transcribed: the stored codes and the labels shown for them. */
export const ROLE_OPTIONS = [
  { value: "AD", label: "Manager" },
  { value: "ED", label: "Editor" },
];

export const CLAIMER_LABEL = "Admin";

/* Transcribed from the live disabled rule on Send Invite. */
export const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ""));

export const canSendInvite = (invite) =>
  Boolean(invite?.name) && Boolean(invite?.email) && validateEmail(invite?.email);

export const ManageRolesModal = ({ onClose }) => {
  const { id } = useParams();
  const { token } = useSelector((state) => state.auth);

  const [users, setUsers] = useState([]);
  /* Transcribed: a counter the list effect watches, bumped after every write
     so the list re-reads. */
  const [refreshKey, setRefreshKey] = useState(0);
  const [invite, setInvite] = useState({ email: "", role: "AD", name: "" });

  const [getUserList] = useGetCompanyUserListMutation();
  const [userInvite, { isLoading: inviting }] = useCreateCompanyUserInviteMutation();
  const [roleUpdate] = useCreateCompanyUserRoleUpdateMutation();
  const [roleDelete] = useCreateCompanyUserRoleDeleteMutation();

  /* Transcribed, argument shape included: the id travels inside `data`. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getUserList({ data: { id }, token });
        if (!cancelled && data) setUsers(data);
      } catch {
        /* The live effect swallows this. Kept: the list simply stays as it
           was, and the modal is still usable for inviting. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getUserList, id, token, refreshKey]);

  /* Transcribed verbatim, including the error-message chain the live handler
     walks to find something to show. */
  const sendInvite = async () => {
    try {
      const data = await userInvite({
        data: { ...invite, company_id: id },
        token,
      }).unwrap();
      if (data) {
        CommonToast.success(`Invitation sent to: ${invite.email}`);
        setRefreshKey((k) => k + 1);
        setInvite({ email: "", role: "AD", name: "" });
      }
    } catch (error) {
      CommonToast.error(
        error?.data?.status ||
          error?.data?.error ||
          (error?.data?.role?.length >= 0 && error?.data?.role[0]) ||
          error?.status
      );
    }
  };

  /* Transcribed: the server's own message is the success toast. */
  const changeRole = async (user, role) => {
    try {
      const res = await roleUpdate({ data: { id: user?.id, role }, token }).unwrap();
      if (res) {
        CommonToast.success(res.status);
        setRefreshKey((k) => k + 1);
      }
    } catch {
      CommonToast.error("Something went wrong");
    }
  };

  const removeUser = async (user) => {
    try {
      const res = await roleDelete({ data: { id: user?.id }, token }).unwrap();
      if (res) {
        CommonToast.success(res.status);
        setRefreshKey((k) => k + 1);
      }
    } catch {
      CommonToast.error("Something went wrong");
    }
  };

  return (
    <Modal modalHandle={onClose}>
      <div className="u1-root w-[min(92vw,600px)] text-left">
        <h2 className="m-0 text-[19px] font-bold text-[var(--ink)] font-sans">
          Manage Roles
        </h2>
        <p className="mt-1 mb-5 text-[13.5px] text-[var(--muted)] font-sans">
          Edit the roles of people who can manage this business.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Input
            id="u1-invite-name"
            label="Name"
            value={invite.name}
            onChange={(e) => setInvite((v) => ({ ...v, name: e.target.value }))}
            placeholder="Enter name"
          />
          <Input
            id="u1-invite-email"
            label="Email"
            type="email"
            value={invite.email}
            onChange={(e) => setInvite((v) => ({ ...v, email: e.target.value }))}
            placeholder="Enter email"
          />
          <SelectField
            id="u1-invite-role"
            label="Role"
            value={invite.role}
            onChange={(e) => setInvite((v) => ({ ...v, role: e.target.value }))}
            options={ROLE_OPTIONS}
          />
        </div>

        <Button
          className="mt-3 w-full"
          onClick={sendInvite}
          /* Transcribed disabled rule: a name, an email, and a valid one. */
          disabled={!canSendInvite(invite) || inviting}
        >
          {inviting ? "Sending…" : "Send Invite"}
        </Button>

        <ul className="mt-6 m-0 flex list-none flex-col p-0">
          {users.length === 0 && (
            <li className="py-6 text-center text-[13.5px] text-[var(--muted)] font-sans">
              No one else has access to this business yet.
            </li>
          )}

          {users.map((user, i) => (
            <li
              key={user?.id ?? `user-${i}`}
              className={cn(
                "flex flex-wrap items-center gap-3 border-b border-[var(--line)] py-3",
                "last:border-b-0"
              )}
            >
              <CompanyMark company={{ name: user?.name }} size="xs" shape="circle" />

              <span className="min-w-0 grow basis-[140px]">
                <span className="block text-[14px] font-semibold text-[var(--ink)] font-sans">
                  {user?.name}
                </span>
                <span className="block text-[12px] text-[var(--muted)] font-sans">
                  {user?.email}
                </span>
              </span>

              {/* Transcribed: the claimer is labelled Admin, and is neither
                  editable nor removable. */}
              {user?.role === "CL" ? (
                <span className="text-[13.5px] font-semibold text-[var(--navy)] font-sans">
                  {CLAIMER_LABEL}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <SelectField
                    id={`u1-role-${user?.id ?? i}`}
                    value={user?.role}
                    onChange={(e) => changeRole(user, e.target.value)}
                    options={ROLE_OPTIONS}
                    size="sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeUser(user)}
                    aria-label={`Remove ${user?.name || "this person"}`}
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
                      "border-[var(--line)] bg-white text-[var(--muted)] transition-colors",
                      "hover:border-[var(--danger)] hover:text-[var(--danger)]"
                    )}
                  >
                    <CloseIcon size={15} />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

export default ManageRolesModal;
